import { Card, Gate, Head, State, useAsync } from "@authoxi/ui-shared";
import { client, signinNonce } from "../api";

/**
 * Sign in with your Agent — for relying parties.
 *
 * **The nonce is short-lived and it is NOT single-use**, and this page says so.
 *
 * The original design said "single-use, short-lived". It is a stateless HMAC over its own
 * expiry, which is deliberate: storing nonces would put a database behind an endpoint whose
 * entire appeal is that a relying party can use it without one. Nothing stateless can be
 * single-use, and `issue_nonce` says exactly that in its own docstring.
 *
 * So the honest framing is that replay is the RP's to own — and telling them plainly is worth
 * more than a word that would make an integrator skip the one control they need to build.
 */
export function Signin() {
  const { data, loading } = useAsync(() => signinNonce());

  return (
    <>
      <Head
        pill="Sign in with your Agent"
        title="Let agents sign in to your service."
        lede="A relying party gets a nonce, the agent signs an assertion over it, and you verify it against the agent's own published keys. You never talk to us, and we never see your users."
      />

      <Card pad="lg">
        <p className="label">The whole flow</p>
        <div className="steps" style={{ marginTop: "var(--s4)" }}>
          {[
            [
              "1",
              "Your service issues a nonce",
              "GET /agent/signin/nonce — short-lived, and stateless so you need no store for it.",
            ],
            [
              "2",
              "The agent signs an assertion",
              "Over your audience and that nonce, with its own key.",
            ],
            [
              "3",
              "You verify it",
              "Against the agent's published JWKS. Offline, no call to us.",
            ],
            [
              "4",
              "You decide about replay",
              "The nonce proves WE issued it, not that it is unused. If you need exactly-once, track it — nothing stateless can do that for you.",
            ],
          ].map(([n, t, b]) => (
            <div className="stepbox" key={n}>
              <span className="n">{n}</span>
              <div>
                <strong className="mono">{t}</strong>
                <p className="xs muted" style={{ margin: "3px 0 0", lineHeight: 1.45 }}>
                  {b}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="hr" />
        <p className="label">A live nonce, right now</p>
        {loading && <State kind="loading" title="Asking for one…" />}
        {data && (
          <pre className="code" style={{ marginTop: "var(--s3)" }} data-testid="signin-nonce">
            {data.nonce}
          </pre>
        )}
        <p className="sm muted">
          Good for {data?.expires_in ?? 300} seconds. It is an HMAC over its own expiry, so we
          can confirm we issued it without having stored anything.
        </p>

        <div className="hr" />
        <pre className="code">{`from authoxi.agentsig.signin import verify_assertion

result = verify_assertion(assertion,
                          audience="https://your-api.com",
                          nonce=nonce,
                          public_key=agent_jwks_key)

if result.ok:
    # result.did — who it is
    # result.principal_did — who it acts for`}</pre>
      </Card>

      <Gate
        client={client}
        source="signin"
        title="Reference implementation"
        body="A drop-in verifier for Express, FastAPI and Next.js, plus the test vectors. Free, and yours whether or not you ever pay us."
        cta="Send me the kit"
      />
    </>
  );
}
